import { useRef, useEffect } from 'react';
import { Image as ImageIcon, Link as LinkIcon } from 'lucide-react';

/**
 * Rich Text Editor Component
 * WYSIWYG editor that preserves the text selection when using the toolbar
 * (toolbar buttons prevent focus loss; prompts save/restore the selection range).
 */
const RichTextEditor = ({ value, onChange, minHeight = '120px', placeholder = '' }) => {
  const editorRef = useRef(null);
  const savedRange = useRef(null);

  // Sync external value changes without destroying the DOM while typing
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const emitChange = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.current && editorRef.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
      return true;
    }
    return false;
  };

  const restoreSelection = () => {
    if (savedRange.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const execCommand = (command, cmdValue = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, cmdValue);
    emitChange();
  };

  const insertLink = () => {
    const hadSelection = saveSelection();
    const selectionText = hadSelection ? savedRange.current.toString() : '';
    const url = prompt("URL de l'enllaç:");
    if (!url) return;
    const title = prompt('Text del rollover (opcional):');
    editorRef.current?.focus();
    restoreSelection();
    const titleAttr = title ? ` title="${title.replace(/"/g, '&quot;')}"` : '';
    document.execCommand(
      'insertHTML',
      false,
      `<a href="${url}"${titleAttr} target="_blank" rel="noopener noreferrer" style="color: #1A4D2E; text-decoration: underline;">${selectionText || url}</a>`
    );
    emitChange();
  };

  const insertImage = () => {
    saveSelection();
    const url = prompt('URL de la imatge (des de Documents/Galeria):');
    if (!url) return;
    const alt = prompt('Text alternatiu (opcional):') || '';
    const width = prompt('Amplada en píxels (deixa buit per automàtic):') || '';
    const widthStyle = width ? `max-width: ${width}px; height: auto;` : 'max-width: 100%; height: auto;';
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand(
      'insertHTML',
      false,
      `<img src="${url}" alt="${alt}" style="${widthStyle} border-radius: 8px; margin: 8px 0;" />`
    );
    emitChange();
  };

  // preventDefault on mousedown keeps focus & selection inside the editor
  const toolbarBtn = (props) => ({
    type: 'button',
    onMouseDown: (e) => e.preventDefault(),
    ...props
  });

  return (
    <div className="border border-[#E5E5E5] rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="bg-[#F9F9F7] border-b border-[#E5E5E5] p-2 flex flex-wrap gap-1">
        {/* Text formatting */}
        <button {...toolbarBtn({ onClick: () => execCommand('bold'), className: 'p-1.5 hover:bg-white rounded font-bold min-w-[28px]', title: 'Negreta (Ctrl+B)' })}>
          B
        </button>
        <button {...toolbarBtn({ onClick: () => execCommand('italic'), className: 'p-1.5 hover:bg-white rounded italic min-w-[28px]', title: 'Cursiva (Ctrl+I)' })}>
          I
        </button>
        <button {...toolbarBtn({ onClick: () => execCommand('underline'), className: 'p-1.5 hover:bg-white rounded underline min-w-[28px]', title: 'Subratllat (Ctrl+U)' })}>
          U
        </button>

        <div className="w-px bg-[#E5E5E5] mx-1" />

        {/* Headers */}
        <button {...toolbarBtn({ onClick: () => execCommand('formatBlock', 'h2'), className: 'p-1.5 hover:bg-white rounded font-bold text-sm min-w-[28px]', title: 'Títol H2' })}>
          H2
        </button>
        <button {...toolbarBtn({ onClick: () => execCommand('formatBlock', 'h3'), className: 'p-1.5 hover:bg-white rounded font-semibold text-sm min-w-[28px]', title: 'Subtítol H3' })}>
          H3
        </button>
        <button {...toolbarBtn({ onClick: () => execCommand('formatBlock', 'p'), className: 'p-1.5 hover:bg-white rounded text-sm min-w-[28px]', title: 'Paràgraf' })}>
          P
        </button>

        <div className="w-px bg-[#E5E5E5] mx-1" />

        {/* Lists */}
        <button {...toolbarBtn({ onClick: () => execCommand('insertUnorderedList'), className: 'p-1.5 hover:bg-white rounded min-w-[28px]', title: 'Llista amb punts' })}>
          • ―
        </button>
        <button {...toolbarBtn({ onClick: () => execCommand('insertOrderedList'), className: 'p-1.5 hover:bg-white rounded min-w-[28px]', title: 'Llista numerada' })}>
          1. ―
        </button>

        <div className="w-px bg-[#E5E5E5] mx-1" />

        {/* Insert */}
        <button {...toolbarBtn({ onClick: insertLink, className: 'p-1.5 hover:bg-white rounded', title: 'Inserir enllaç' })} data-testid="rte-insert-link-btn">
          <LinkIcon className="w-4 h-4" />
        </button>
        <button {...toolbarBtn({ onClick: insertImage, className: 'p-1.5 hover:bg-white rounded', title: 'Inserir imatge' })} data-testid="rte-insert-image-btn">
          <ImageIcon className="w-4 h-4" />
        </button>

        <div className="w-px bg-[#E5E5E5] mx-1" />

        {/* Alignment */}
        <button {...toolbarBtn({ onClick: () => execCommand('justifyLeft'), className: 'p-1.5 hover:bg-white rounded text-xs min-w-[28px]', title: 'Alinear esquerra' })}>
          ≡←
        </button>
        <button {...toolbarBtn({ onClick: () => execCommand('justifyCenter'), className: 'p-1.5 hover:bg-white rounded text-xs min-w-[28px]', title: 'Centrar' })}>
          ≡↔
        </button>
        <button {...toolbarBtn({ onClick: () => execCommand('justifyRight'), className: 'p-1.5 hover:bg-white rounded text-xs min-w-[28px]', title: 'Alinear dreta' })}>
          →≡
        </button>
      </div>

      {/* Editor area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={emitChange}
        onBlur={emitChange}
        className="p-4 focus:outline-none prose prose-sm max-w-none"
        style={{ minHeight, lineHeight: '1.7' }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
        data-testid="rte-editor-area"
      />
    </div>
  );
};

export default RichTextEditor;
